import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { outputApi, pipelinesApi } from "@/lib/apiClient";
import { PageContainer, PageHeader } from "@/components/Layout";
import { Pager, usePager } from "@/components/Pager";
import { Mono, CodePill } from "@/components/Atoms";
import CodeEditor from "@/components/CodeEditor";
import StepListEditor from "@/components/StepListEditor";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useCan } from "@/lib/rbac";

export function OutputDestinations() {
  const qc = useQueryClient();
  const canWrite = useCan("write:outputs");
  const list = useQuery({ queryKey: ["outputs"], queryFn: outputApi.list });
  const [modal, setModal] = useState(null);
  const cM = useMutation({ mutationFn: outputApi.create, onSuccess: () => { toast.success("Saved"); qc.invalidateQueries({ queryKey: ["outputs"] }); setModal(null); } });
  const dM = useMutation({ mutationFn: outputApi.remove, onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["outputs"] }); } });
  const pager = usePager(list.data || [], { defaultSize: 50, sizes: [25, 50, 100] });

  return (
    <PageContainer>
      <PageHeader
        title="Output destinations"
        subtitle="Where parsed LoRaWAN frames are forwarded after pipelining."
        actions={
          canWrite ? (
            <button data-testid="output-new" onClick={() => setModal({ name: "", type: "s3", config: {} })} className="bg-[#3730A3] text-white rounded-md px-3 py-2 text-sm hover:bg-indigo-900 inline-flex items-center gap-1.5"><Plus className="w-4 h-4" /> New destination</button>
          ) : null
        }
      />
      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
            <tr><th className="text-left p-3 font-semibold">Name</th><th className="text-left p-3 font-semibold">Type</th><th className="text-left p-3 font-semibold">Config</th><th></th></tr>
          </thead>
          <tbody>
            {pager.pageRows.map((o) => (
              <tr key={o.id} className="border-t border-zinc-100">
                <td className="p-3 font-medium">{o.name}</td>
                <td className="p-3"><CodePill>{o.type}</CodePill></td>
                <td className="p-3"><Mono className="text-zinc-600">{JSON.stringify(o.config)}</Mono></td>
                <td className="p-3 text-right">
                  {canWrite && (
                    <button onClick={() => { if (window.confirm("Delete?")) dM.mutate(o.id); }} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>
                  )}
                </td>
              </tr>
            ))}
            {pager.total === 0 && (<tr><td colSpan={4} className="py-8 text-center text-sm text-zinc-500">No output destinations.</td></tr>)}
          </tbody>
        </table>
        <Pager {...pager} testidPrefix="output-pager" />
      </div>
      {modal && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center px-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-lg p-5 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading text-lg font-bold mb-3">New destination</h2>
            <form onSubmit={(e) => { e.preventDefault(); try { cM.mutate({ ...modal, config: JSON.parse(modal._cfg || "{}") }); } catch { toast.error("Invalid JSON"); } }} className="space-y-3">
              <label className="block">
                <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">name</span>
                <input className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm" value={modal.name} onChange={(e) => setModal({ ...modal, name: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">type</span>
                <input className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm font-mono" value={modal.type} onChange={(e) => setModal({ ...modal, type: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">config (JSON)</span>
                <div className="mt-1.5">
                  <CodeEditor value={modal._cfg || JSON.stringify(modal.config || {}, null, 2)} onChange={(v) => setModal({ ...modal, _cfg: v })} height="180px" language="json" />
                </div>
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setModal(null)} className="border border-zinc-200 rounded-md px-3 py-1.5 text-sm">Cancel</button>
                <button type="submit" className="bg-[#3730A3] text-white rounded-md px-3 py-1.5 text-sm">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PageContainer>
  );
}

export function ParserPipelines() {
  const qc = useQueryClient();
  const canWrite = useCan("write:pipelines");
  const list = useQuery({ queryKey: ["pipelines"], queryFn: pipelinesApi.list });
  const [modal, setModal] = useState(null);
  const cM = useMutation({ mutationFn: pipelinesApi.create, onSuccess: () => { toast.success("Saved"); qc.invalidateQueries({ queryKey: ["pipelines"] }); setModal(null); } });
  const uM = useMutation({ mutationFn: ({ id, data }) => pipelinesApi.update(id, data), onSuccess: () => { toast.success("Saved"); qc.invalidateQueries({ queryKey: ["pipelines"] }); setModal(null); } });
  const dM = useMutation({ mutationFn: pipelinesApi.remove, onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["pipelines"] }); } });
  const pager = usePager(list.data || [], { defaultSize: 50, sizes: [25, 50, 100] });

  return (
    <PageContainer>
      <PageHeader
        title="Parser pipelines"
        subtitle="Ordered processing steps for LoRaWAN decoders."
        actions={canWrite ? <button data-testid="pipeline-new" onClick={() => setModal({ name: "", steps: [] })} className="bg-[#3730A3] text-white rounded-md px-3 py-2 text-sm hover:bg-indigo-900 inline-flex items-center gap-1.5"><Plus className="w-4 h-4" /> New pipeline</button> : null}
      />
      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
            <tr><th className="text-left p-3 font-semibold">Name</th><th className="text-left p-3 font-semibold">Steps</th><th></th></tr>
          </thead>
          <tbody>
            {pager.pageRows.map((p) => (
              <tr key={p.id} className="border-t border-zinc-100">
                <td className="p-3 font-medium">{p.name}</td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1">
                    {(p.steps || []).map((s, i) => (
                      <CodePill key={i}>{i + 1}. {s?.name || s?.parser || "step"}</CodePill>
                    ))}
                    {(p.steps || []).length === 0 && <span className="text-xs text-zinc-400">no steps</span>}
                  </div>
                </td>
                <td className="p-3 text-right">
                  {canWrite && (
                    <>
                      <button
                        data-testid={`pipeline-edit-${p.id}`}
                        onClick={() => setModal({ id: p.id, name: p.name, steps: Array.isArray(p.steps) ? p.steps : [] })}
                        className="p-1.5 rounded hover:bg-zinc-100"
                      ><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => { if (window.confirm("Delete?")) dM.mutate(p.id); }} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {pager.total === 0 && (<tr><td colSpan={3} className="py-8 text-center text-sm text-zinc-500">No pipelines.</td></tr>)}
          </tbody>
        </table>
        <Pager {...pager} testidPrefix="pipeline-pager" />
      </div>
      {modal && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center px-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-lg p-5 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading text-lg font-bold mb-3" data-testid="pipeline-modal-title">
              {modal.id ? `Edit ${modal.name || "pipeline"}` : "New pipeline"}
            </h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!modal.name.trim()) { toast.error("Name is required"); return; }
                const payload = { name: modal.name.trim(), steps: modal.steps || [] };
                if (modal.id) uM.mutate({ id: modal.id, data: payload });
                else cM.mutate(payload);
              }}
              className="space-y-4"
            >
              <label className="block">
                <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">name</span>
                <input
                  data-testid="pipeline-input-name"
                  className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm"
                  value={modal.name}
                  onChange={(e) => setModal({ ...modal, name: e.target.value })}
                />
              </label>
              <div>
                <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">steps</span>
                <div className="mt-1.5">
                  <StepListEditor
                    value={modal.steps || []}
                    onChange={(steps) => setModal({ ...modal, steps })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
                <button type="button" onClick={() => setModal(null)} className="border border-zinc-200 rounded-md px-3 py-1.5 text-sm">Cancel</button>
                <button data-testid="pipeline-save" type="submit" className="bg-[#3730A3] text-white rounded-md px-3 py-1.5 text-sm">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PageContainer>
  );
}
